import { type Node, type Edge } from '@xyflow/react';
import {
  IEdgeExtraData,
  CABLE_TYPE_LABELS,
} from 'src/modules/network-configuration/edges/types';
import {
  EDataType,
  type NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';

import {
  sortPortsROQSTAR,
  formatVlanIdsWithUt,
  uppercaseEveryDashWord,
} from '../export-helpers';
import { getEdgeInfo, getEdgeName, getInfoConnected } from '../node';
import {
  type NodeExportData,
  type CableTypeExportRow,
  type CableTypeExportItem,
  type PortAssignmentExportData,
} from './types';

// Extract node data for export
export const extractNodeData = (
  nodes: Node[],
  edges: Edge[],
  type?: EDataType,
): NodeExportData[] => {
  const filtered = type
    ? nodes.filter(
        (node) => (node.data?.category as NodeCategoryModel)?.type === type,
      )
    : nodes;

  const data = filtered.map((switchNode) => {
    const category = switchNode.data?.category as NodeCategoryModel;
    const ipInterfaces = category?.ipInterfaces;

    const vlanIds: number[] =
      category?.ipInterfaces?.map((ipInterface) => ipInterface.vlanId) || [];

    let ips: { ip: string; subnetMask: string }[] = [];
    if (ipInterfaces && ipInterfaces.length > 0) {
      ips = ipInterfaces
        .filter((ipInterface) => ipInterface.ip && ipInterface.ip.trim() !== '')
        .map((ipInterface) => ({
          ip: ipInterface.ip || '',
          subnetMask: ipInterface.subnetMask || '',
        }));
    } else if (category?.ip) {
      ips.push({
        ip: category.ip || '',
        subnetMask: category.subnetMask || '',
      });
    }

    const isCurrentNodeSwitch = category?.type === EDataType.Switch;

    const portsRaw =
      category?.ports?.map((port) => {
        const connectedEdge = getInfoConnected(
          switchNode.id,
          port.id,
          edges,
          nodes,
        );
        const edgeData = connectedEdge?.edge?.data as
          | IEdgeExtraData
          | undefined;
        const connectedCat = connectedEdge?.node?.data?.category as
          | NodeCategoryModel
          | undefined;
        const isDevice = connectedCat?.type === EDataType.Device;
        const connectedDevicePoeEnabled = !!(
          isDevice && connectedCat?.poe?.enable
        );
        const dhcpEnabled = !!(
          isDevice &&
          (connectedCat?.dhcp ||
            connectedCat?.ipInterfaces?.some((i) => i.dhcp))
        );

        const isSwitch = connectedCat?.type === EDataType.Switch;

        return {
          name: port.id,
          connectNode: connectedCat?.name || '',
          connectPort: isSwitch ? connectedEdge?.port?.id : '',
          vlanIds: edgeData?.vlanIds ?? [],
          untaggedVlan: edgeData?.untaggedVlan,
          poeEnabled: isCurrentNodeSwitch
            ? !!(port.isPoE && connectedDevicePoeEnabled)
            : !!(category?.poe?.enable && port.isPoE),
          isPoeCapable: !!port.isPoE,
          dhcpEnabled,
        };
      }) || [];

    const Port =
      category?.type === EDataType.Device
        ? [...portsRaw].sort((a, b) => {
            const nodeCmp = (a.connectNode || '').localeCompare(
              b.connectNode || '',
              undefined,
              { numeric: true },
            );
            if (nodeCmp !== 0) return nodeCmp;
            return sortPortsROQSTAR(a.connectPort || '', b.connectPort || '');
          })
        : [...portsRaw].sort((a, b) => sortPortsROQSTAR(a.name, b.name));

    return {
      Name: category?.name || 'Unknown',
      Model: uppercaseEveryDashWord(switchNode?.type || 'Unknown'),
      Type: category?.type || 'Unknown',
      Notes: category?.notes?.trim() || '',
      enabledPoe: category?.poe?.enable || false,
      IP: ips,
      Port,
      vlanIds: (vlanIds || []).sort((a, b) => a - b),
    };
  });

  // Sort data by target node name
  data.sort(
    (a, b) =>
      a.Port[0]?.connectNode?.localeCompare(
        b.Port[0]?.connectNode || '',
        undefined,
        {
          numeric: true,
        },
      ) ||
      sortPortsROQSTAR(
        a.Port[0].connectPort || '',
        b.Port[0].connectPort || '',
      ),
  );
  return data;
};

/** Devices export: all devices plus one row per switch (no connect node/port). */
export const extractDevicesExportData = (
  nodes: Node[],
  edges: Edge[],
): NodeExportData[] => {
  const devices = extractNodeData(nodes, edges, EDataType.Device);
  const switches = extractNodeData(nodes, edges, EDataType.Switch)
    .map((s) => ({
      ...s,
      Port: [{ name: '', connectNode: '', connectPort: '' }],
      enabledPoe: undefined,
    }))
    .sort((a, b) => a.Name.localeCompare(b.Name, undefined, { numeric: true }));
  return [...devices, ...switches];
};

function getSwitchPortPoE(
  node: Node | undefined,
  portId: string | undefined,
): boolean {
  if (!node || !portId) return false;
  const cat = node.data?.category as NodeCategoryModel | undefined;
  if (cat?.type !== EDataType.Switch) return false;
  const port = cat.ports?.find((p) => p.id === portId);
  return !!port?.isPoE;
}

function getDevicePoEEnabled(node: Node | undefined): boolean {
  if (!node) return false;
  const cat = node.data?.category as NodeCategoryModel | undefined;
  if (cat?.type !== EDataType.Device) return false;
  return !!cat.poe?.enable;
}

function getDeviceDhcp(node: Node | undefined): boolean {
  if (!node) return false;
  const cat = node.data?.category as NodeCategoryModel | undefined;
  if (cat?.type !== EDataType.Device) return false;
  return !!(cat.dhcp || cat.ipInterfaces?.some((i) => i.dhcp));
}

// Extract port assignment data for export
export const extractPortAssignmentData = (
  nodes: Node[],
  edges: Edge[],
): PortAssignmentExportData[] => {
  const data = edges.map((edge) => {
    const edgeInfo = getEdgeInfo(edge, nodes);
    const { sourceNode, targetNode, sourcePort, targetPort } = edgeInfo;
    const edgeData = edge.data as IEdgeExtraData | undefined;
    const vlanIds = edgeData?.vlanIds ?? [];
    const untaggedVlan = edgeData?.untaggedVlan;

    const isTargetSwitch =
      (targetNode?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Switch;

    const isSourceSwitch =
      (sourceNode?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Switch;

    const targetIsDevice =
      (targetNode?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Device;
    const sourceIsDevice =
      (sourceNode?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Device;

    const isSwitchSwitch = isSourceSwitch && isTargetSwitch;
    let poe = '-';
    if (!isSwitchSwitch) {
      let deviceNode: Node | undefined;
      let switchNode: Node | undefined;
      let switchPort: string | undefined;
      if (sourceIsDevice) {
        deviceNode = sourceNode;
        if (isTargetSwitch) {
          switchNode = targetNode;
          switchPort = targetPort;
        }
      } else if (targetIsDevice) {
        deviceNode = targetNode;
        if (isSourceSwitch) {
          switchNode = sourceNode;
          switchPort = sourcePort;
        }
      }

      const devicePoeEnabled = getDevicePoEEnabled(deviceNode);
      const switchPortPoe = getSwitchPortPoE(switchNode, switchPort);
      poe = devicePoeEnabled && switchPortPoe ? 'Yes' : 'No';
    }

    return {
      Name: edgeData?.customDescription || getEdgeName(edge.id, nodes),
      Target: (targetNode?.data?.category as NodeCategoryModel)?.name || '',
      'Target Port': isTargetSwitch ? targetPort || '' : '',
      Source: (sourceNode?.data?.category as NodeCategoryModel)?.name || '',
      'Source Port': isSourceSwitch ? sourcePort || '' : '',
      vlanIds,
      untaggedVlan,
      vlanIdsFormatted: formatVlanIdsWithUt(vlanIds, untaggedVlan),
      poe,
      dhcp:
        getDeviceDhcp(targetNode) || getDeviceDhcp(sourceNode) ? 'Yes' : 'No',
    };
  });

  // Sort data by target node name
  data.sort(
    (a, b) =>
      a.Target.localeCompare(b.Target, undefined, { numeric: true }) ||
      sortPortsROQSTAR(a['Target Port'], b['Target Port']),
  );

  return data;
};

/** Group edges by cableType (switchConnector + deviceConnector) and build export rows */
export const extractCableTypeData = (
  edges: Edge[],
  nodes: Node[],
): CableTypeExportRow[] => {
  const groups = new Map<
    string,
    {
      typeLabel: string;
      items: CableTypeExportItem[];
    }
  >();

  edges.forEach((edge) => {
    const data = edge.data as IEdgeExtraData | undefined;
    const ct = data?.cableType;
    if (!data || !ct) return;

    const { sourceNode, targetNode, sourcePort, targetPort } = getEdgeInfo(
      edge,
      nodes,
    );
    const sourceName =
      (sourceNode?.data?.category as NodeCategoryModel)?.name ||
      sourceNode?.id ||
      'Unknown';
    const targetName =
      (targetNode?.data?.category as NodeCategoryModel)?.name ||
      targetNode?.id ||
      'Unknown';

    const groupKey = `${ct.switchConnector}-${ct.deviceConnector}`;
    const typeLabel = `${CABLE_TYPE_LABELS[ct.switchConnector] ?? ct.switchConnector} / ${CABLE_TYPE_LABELS[ct.deviceConnector] ?? ct.deviceConnector}`;
    const length = ct.length ?? 0;

    if (groups.has(groupKey)) {
      const g = groups.get(groupKey)!;
      g.items.push({
        length,
        sourceName,
        sourceType: (sourceNode?.data?.category as NodeCategoryModel)?.type,
        sourcePort,
        targetName,
        targetPort,
        targetType: (targetNode?.data?.category as NodeCategoryModel)?.type,
      });
    } else {
      groups.set(groupKey, {
        typeLabel,
        items: [
          {
            length,
            sourceName,
            sourcePort,
            sourceType: (sourceNode?.data?.category as NodeCategoryModel)?.type,
            targetName,
            targetPort,
            targetType: (targetNode?.data?.category as NodeCategoryModel)?.type,
          },
        ],
      });
    }
  });

  const dataRows = Array.from(groups.entries())
    .map(([, g]) => ({
      Type: g.typeLabel,
      'Length (M)': g.items.map((item) => ({
        length: item.length,
        sourceName: item.sourceName,
        sourcePort: item.sourcePort,
        sourceType: item.sourceType,
        targetName: item.targetName,
        targetPort: item.targetPort,
        targetType: item.targetType,
      })),
      Quantity: g.items.length,
    }))
    .sort((a, b) => a.Type.localeCompare(b.Type));

  const powerCablesLength = nodes.filter(
    (n) =>
      (n.data?.category as NodeCategoryModel)?.type === EDataType.Switch &&
      (n.data?.category as NodeCategoryModel)?.powerCableLength! > 0,
  );
  if (powerCablesLength.length > 0) {
    const powerCableRow: CableTypeExportRow = {
      Type: 'Power Cable',
      'Length (M)': powerCablesLength.map((n) => ({
        length: (n.data?.category as NodeCategoryModel)?.powerCableLength ?? 0,
        sourceName:
          String((n.data?.category as NodeCategoryModel)?.name) ??
          String(n.id) ??
          'Unknown',
      })),
      Quantity: powerCablesLength.length,
    };
    return [...dataRows, powerCableRow];
  }

  return [...dataRows];
};

const formatLengthItem = (
  item: CableTypeExportItem,
  boldAsHtml: boolean,
): string => {
  const isSourceDevice = item.sourceType === EDataType.Device;
  const isTargetDevice = item.targetType === EDataType.Device;
  const sourceDisplay =
    !isSourceDevice && item.sourcePort
      ? `${item.sourceName}-${item.sourcePort}`
      : item.sourceName;
  const targetDisplay =
    !isTargetDevice && item.targetPort
      ? `${item.targetName}-${item.targetPort}`
      : item.targetName;
  const lengthPart = boldAsHtml
    ? `<b>${item.length}m</b> `
    : `${item.length}m `;
  return `${lengthPart} (${sourceDisplay}${targetDisplay ? ` <-> ${targetDisplay}` : ''})`;
};

export const buildLengthMString = (
  items: CableTypeExportItem[],
  type: 'pdf' | 'html' | 'excel' = 'html',
): string => {
  if (items.length === 0) return '-';
  const boldAsHtml = type === 'html';
  return items.map((item) => formatLengthItem(item, boldAsHtml)).join('\n');
};
